import React, { memo, useEffect, useRef, useState } from 'react'
import {
  CustomAgTable,
  ExportButtonDropdown,
  SORT_KEYS,
  TableColumnType,
  tableFilterType,
  Tooltip,
} from '@ui-kit'
import {
  DataTypeStore,
  hideLoader,
  SearchCriteria,
  showLoader,
  TableFilterObjectBody,
  appendTenantIdToPath,
  addToaster,
  exportEntity2ChildData,
  openWithTenant,
  ToasterType,
  getEntityDataByEntityNameApiForFilter,
  formatDataObject,
} from '@app-store'
import { isEmpty, uniqueId } from 'lodash'
import { FieldValues } from 'react-hook-form'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import { JsonStructureDetailsModal } from './JsonStructureModal'
import { AssociatedCaseIdsModal } from './AssociatedCaseIdsModal'
import {
  ENTITY_ID,
  EXPERIAN_ERROR_MESSAGE,
  FIELD_VALUE_CONSTANTS,
  INTEGRATION_LOGS,
  NAMESPACES,
  CTI_COMMUNICATION_SELECT,
  CTI_LISTING_SELECT,
  TIMESTAMP_SEARCH_KEYS,
} from '../../utils/constants'
import {
  buildIntegrationLogsExportPayload,
  CommunicationLogCase,
  fetchAssociatedCasesByResourceId,
  fetchGenericPayloadIntegrationLogsListing,
  getCommunicationRecordIdFromLogItem,
  getFieldNameForIntegration,
  getParentEntityForIntegration,
  getProcessingDurationSeconds,
  getRootEntityForIntegration,
  isCtiIntegration,
  isIvrIntegration,
  mapCtiIntegrationRow,
  normalizeIntegrationLogCaseIds,
  mergeGenericPayloadLogItems,
  normalizeCtiListingResponse,
  resolveOcrChildFaxContextForCase,
  toCommunicationLogCases,
} from '../../utils/integrationLogs.utils'
import { downloadCSV } from '../../utils/helperFunctions'
import {
  IntegrationLogReq,
  IntegrationLogsTableProps,
  IntegrationType,
  ROOT_ENTITY,
  SortDirectionState,
} from '../../types/types'
import {
  formatLocalDateWithZone,
  getCurrentUTCDate,
} from '../../utils/date.utils'
import { useParams } from 'react-router-dom'
import moment from 'moment-timezone'

const mapIntegrationRow = (item: FieldValues): DataTypeStore =>
  ({
    api: item?.integration_functionality?.identifier,
    req: {
      response: item?.response_body_json,
      request: item?.request_body_json,
      error: item?.response_error_message,
    },
    status: item?.status,
    transaction_id: item?.request_id,
    status_code: item?.http_status,
    request_type: item?.integration_functionality?.request_type,
    request_timestamp: formatLocalDateWithZone(item?.created_at as string),
    response_timestamp: formatLocalDateWithZone(item?.modified_at as string),
    processing_duration: getProcessingDurationSeconds(
      item?.created_at as string,
      item?.modified_at as string
    ),
    entity_id: item?.patient?.patient_key,
    patient_record_id: item.patient?.id,
    case_id: item.case?.case_key,
    case_record_id: item.case?.id,
    case_ids: normalizeIntegrationLogCaseIds(item?.case_ids),
    communication_record_id: getCommunicationRecordIdFromLogItem(item),
    communication_key: item?.communication?.communication_id,
    program: item?.case?.programs?.operational_program_name,
    product: item?.case?.products?.trademarked_name,
    triggered_by: item.created_by,
  } as unknown as DataTypeStore)

const getRowFilterValue = (
  row: DataTypeStore,
  accessor: string
): string | number | boolean | undefined => {
  const value = row[accessor]
  if (value === null || value === undefined) return undefined
  return value as string | number | boolean
}

const IntegrationLogRequestCell: React.FC<{ row: DataTypeStore }> = ({
  row,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const req = row?.req as IntegrationLogReq | undefined
  const responsePayload: Record<string, unknown> = req?.error
    ? { message: req.error }
    : req?.response ?? {}

  return (
    <div>
      <JsonStructureDetailsModal
        isOpen={isOpen}
        closeModal={() => setIsOpen(false)}
        request={req?.request ?? {}}
        response={responsePayload}
      />
      <span
        className="text-sm text-secondary-500  cursor-pointer"
        onClick={() => setIsOpen((open) => !open)}
      >
        {INTEGRATION_LOGS.VIEW_REQUEST}
      </span>
    </div>
  )
}

const CaseIdLinkCell: React.FC<{ row: DataTypeStore }> = ({ row }) => {
  const tenantId = useAppSelector(
    (state: RootState) => state.authUser?.selectedClientTenant?.id
  )
  const caseKey = row?.case_id as string | undefined
  const caseRecordId = (row as DataTypeStore & { case_record_id?: number })
    ?.case_record_id

  if (!caseKey) return <span>--</span>
  if (!caseRecordId) return <span>{caseKey}</span>

  return (
    <span
      className="text-sm font-medium text-border-50  cursor-pointer"
      onClick={() =>
        openWithTenant(
          appendTenantIdToPath(
            `entity/${ENTITY_ID.CASE}/workflow/review/${caseRecordId}`,
            tenantId
          ),
          '_blank'
        )
      }
    >
      {caseKey}
    </span>
  )
}

const AssociatedCaseIdsCell: React.FC<{
  onViewIds: (resourceId?: string | number) => void
}> = ({ onViewIds }) => (
  <span
    className="text-sm text-secondary-500 cursor-pointer"
    onClick={(event) => {
      event.stopPropagation()
      onViewIds()
    }}
  >
    {INTEGRATION_LOGS.VIEW_ASSOCIATED_CASE_IDS}
  </span>
)

const buildAssociatedCaseIdsColumn = (
  onView: (resourceId?: string | number) => void
): TableColumnType<DataTypeStore> => ({
  header: INTEGRATION_LOGS.ASSOCIATED_CASE_IDS_TITLE,
  accessor: 'communication_record_id',
  type: 'component',
  renderComponent: (row: DataTypeStore) => (
    <AssociatedCaseIdsCell
      onViewIds={() =>
        onView(
          (
            row as DataTypeStore & {
              communication_record_id?: string | number
            }
          )?.communication_record_id
        )
      }
    />
  ),
})

const buildIvrAssociatedCaseIdsColumn = (
  onViewCaseIds: (caseIds: unknown) => void
): TableColumnType<DataTypeStore> => ({
  header: INTEGRATION_LOGS.ASSOCIATED_CASE_IDS_TITLE,
  accessor: 'case_ids',
  type: 'component',
  renderComponent: (row: DataTypeStore) => (
    <AssociatedCaseIdsCell onViewIds={() => onViewCaseIds(row?.case_ids)} />
  ),
})

const PatientIdLinkCell: React.FC<{ row: DataTypeStore }> = ({ row }) => {
  const tenantId = useAppSelector(
    (state: RootState) => state.authUser?.selectedClientTenant?.id
  )
  const patientKey = row?.entity_id as string | undefined
  const patientRecordId = (
    row as DataTypeStore & { patient_record_id?: number }
  )?.patient_record_id

  if (!patientKey) return <span>--</span>
  if (!patientRecordId) return <span>{patientKey}</span>

  return (
    <span
      className="text-sm font-medium text-border-50  cursor-pointer"
      onClick={() =>
        openWithTenant(
          appendTenantIdToPath(
            `entities/${ENTITY_ID.PATIENT}/view/${patientRecordId}`,
            tenantId
          ),
          '_blank'
        )
      }
    >
      {patientKey}
    </span>
  )
}

const IntegrationLogsTable: React.FC<IntegrationLogsTableProps> = ({
  selectedIntegration,
  tableName = 'integration_logs',
  defaultPageSize = FIELD_VALUE_CONSTANTS.TWENTY_FIVE,
  isCaseTab = false,
}) => {
  const { caseId: paramCaseId, id } = useParams()
  const caseId = paramCaseId ?? id
  const [intergrationData, setIntergrationData] = useState<DataTypeStore[]>()
  const [associatedCasesModal, setAssociatedCasesModal] = useState<{
    isOpen: boolean
    cases: CommunicationLogCase[]
    title?: string
  }>({ isOpen: false, cases: [] })
  const [pageNumber, setPageNumber] = useState<number>(
    FIELD_VALUE_CONSTANTS.ZERO
  )
  const [pageSize, setPageSize] = useState<number>(defaultPageSize)
  const [totalPages, setTotalPages] = useState<number>(
    FIELD_VALUE_CONSTANTS.ONE
  )
  const [totalElements, setTotalElements] = useState<number>(
    FIELD_VALUE_CONSTANTS.ZERO
  )
  const [numberOfElements, setNumberOfElements] = useState<number>(
    FIELD_VALUE_CONSTANTS.ZERO
  )
  const [sortDirection, setSortDirection] = useState<SortDirectionState>({
    key: '',
    keyName: '',
    direction: SORT_KEYS.Disabled,
    isAsc: SORT_KEYS.Disabled,
  })
  const [apiFilter, setApiFilter] = useState<TableFilterObjectBody>({
    fields: null,
    search_criteria: {},
  })
  const currentFilterPayload = useRef<SearchCriteria[]>([])
  const pageInformation = useRef<{ page: number; size: number }>({
    page: FIELD_VALUE_CONSTANTS.ZERO,
    size: defaultPageSize,
  })
  const prevIntegrationValue = useRef<string>()
  const fetchGenerationRef = useRef(0)
  const dispatch = useAppDispatch()
  const isIvrSelected = isIvrIntegration(selectedIntegration)

  const handleViewAssociatedCaseIds = async (resourceId?: string | number) => {
    if (!resourceId) {
      setAssociatedCasesModal({
        isOpen: true,
        cases: [],
        title: INTEGRATION_LOGS.ASSOCIATED_CASE_IDS_TITLE,
      })
      return
    }

    try {
      dispatch(showLoader())
      const cases = await fetchAssociatedCasesByResourceId(resourceId)
      setAssociatedCasesModal({
        isOpen: true,
        cases,
        title: INTEGRATION_LOGS.ASSOCIATED_CASE_IDS_TITLE,
      })
    } catch (error) {
      console.error(error)
      setAssociatedCasesModal({
        isOpen: true,
        cases: [],
        title: INTEGRATION_LOGS.ASSOCIATED_CASE_IDS_TITLE,
      })
    } finally {
      dispatch(hideLoader())
    }
  }

  const handleViewIvrCaseIds = (caseIds: unknown) => {
    setAssociatedCasesModal({
      isOpen: true,
      cases: toCommunicationLogCases(caseIds),
      title: INTEGRATION_LOGS.ASSOCIATED_CASE_IDS_TITLE,
    })
  }

  const isTimestampSearchKey = (searchKey?: string): boolean => {
    return TIMESTAMP_SEARCH_KEYS.includes((searchKey ?? '').toUpperCase())
  }

  const formatMomentAsApiUtc = (date: moment.Moment): string => {
    return `${date.utc().format('YYYY-MM-DDTHH:mm:ss')}.000000Z`
  }

  const formatDateTimeForApi = (value: string): string => {
    if (!value) return value

    const browserTimeZone = moment.tz.guess()

    const cleanedValue = value
      .replace(/\s*\(UTC\s*[+-]\d{2}:\d{2}\)\s*$/i, '')
      .replace(/\s*UTC\s*[+-]\d{2}:\d{2}\s*$/i, '')
      .replace(/\s+[A-Z]{2,5}\s*$/i, '')
      .trim()

    const apiUtcPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?Z$/i

    if (apiUtcPattern.test(cleanedValue)) {
      const parsedUtcDate = moment.utc(cleanedValue)

      if (!parsedUtcDate.isValid()) return value

      return formatMomentAsApiUtc(parsedUtcDate)
    }
    const tableUtcPattern = /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}(:\d{2})?$/

    if (tableUtcPattern.test(cleanedValue)) {
      const parsedUtcDate = moment.utc(
        cleanedValue,
        ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm'],
        true
      )

      if (!parsedUtcDate.isValid()) return value

      return formatMomentAsApiUtc(parsedUtcDate)
    }
    const parsedLocalDate = moment.tz(
      cleanedValue,
      [
        'MM/DD/YYYY HH:mm:ss',
        'MM/DD/YYYY HH:mm',
        'YYYY-MM-DDTHH:mm:ss',
        'YYYY-MM-DDTHH:mm',
      ],
      true,
      browserTimeZone
    )

    if (!parsedLocalDate.isValid()) return value

    return formatMomentAsApiUtc(parsedLocalDate)
  }

  const normalizeCriteriaForApi = (
    criteriaArray: SearchCriteria[]
  ): SearchCriteria[] => {
    return criteriaArray
      .filter((criteria) => criteria && Object.keys(criteria).length > 0)
      .map((criteria) => {
        if (!isTimestampSearchKey(criteria?.search_key)) {
          return criteria
        }

        return {
          ...criteria,
          search_value: criteria.search_value.map(formatDateTimeForApi),
        }
      })
  }

  const fetchIntegrationData = async (
    integration: IntegrationType,
    page: number,
    size: number,
    sort?: SortDirectionState,
    searchCriteria: SearchCriteria | SearchCriteria[] = [],
    fetchFilters = false
  ) => {
    const isCti = isCtiIntegration(integration)
    const criteriaArray = Array.isArray(searchCriteria)
      ? searchCriteria
      : [searchCriteria]
    const validCriteria = normalizeCriteriaForApi(criteriaArray)

    dispatch(showLoader())

    try {
      if (!fetchFilters) {
        currentFilterPayload.current = validCriteria
      }

      const rootEntity = getRootEntityForIntegration(isCti)

      const rootFilters: formatDataObject[] = []
      const relatedFiltersByEntity: Record<string, formatDataObject[]> = {}

      validCriteria.forEach((criteria) => {
        const searchKey = criteria?.search_key ?? ''
        const parentEntity = getParentEntityForIntegration(searchKey, isCti)

        const filter = {
          operation: criteria?.operator,
          field_name: getFieldNameForIntegration(searchKey, isCti),
          values: criteria?.search_value,
        }

        if (parentEntity === rootEntity) {
          rootFilters.push(filter)
        } else {
          ;(relatedFiltersByEntity[parentEntity] ??= []).push(filter)
        }
      })

      const isSortActive =
        sort?.direction !== undefined &&
        sort.direction !== SORT_KEYS.Disabled &&
        !!sort.keyName

      const sortEntity = isSortActive
        ? getParentEntityForIntegration(sort?.key ?? '', isCti)
        : null

      const sortCriteria: formatDataObject[] = isSortActive
        ? [
            {
              direction: `${sort?.direction.toUpperCase()}`,
              field_name: sort?.keyName ?? '',
            },
          ]
        : []

      const buildRelatedFilters = (
        entity: string,
        extraCriteria: formatDataObject[] = []
      ) => {
        const criteria = [
          ...extraCriteria,
          ...(relatedFiltersByEntity[entity] ?? []),
        ]

        const filterBlocks: { criteria: formatDataObject[] }[] = []

        if (criteria.length) {
          filterBlocks.push({ criteria })
        }

        if (sortEntity === entity && sortCriteria.length) {
          filterBlocks.push({ criteria: sortCriteria })
        }

        return filterBlocks.length ? { filters: filterBlocks } : {}
      }

      if (isCti) {
        const filterPayload = [
          {
            logical_operator: 'AND',
            criteria: [...rootFilters],
          },
          {
            criteria: sortEntity === rootEntity ? sortCriteria : [],
          },
        ]

        const response = await getEntityDataByEntityNameApiForFilter({
          payload: {
            entity_name: INTEGRATION_LOGS.CTI_ENTITY_NAME,
            select: [...CTI_LISTING_SELECT],
            page: !fetchFilters ? page : pageInformation.current.page,
            size: !fetchFilters ? size : pageInformation.current.size,
            filters: filterPayload,
            related_entities: {
              communication: {
                select: [...CTI_COMMUNICATION_SELECT],
                ...buildRelatedFilters('communication'),
                related_entities: {
                  programs: {
                    select: ['id', 'operational_program_name'],
                    ...buildRelatedFilters('programs'),
                  },
                  products: {
                    select: ['id', 'trademarked_name'],
                    ...buildRelatedFilters('products'),
                  },
                },
              },
            },
          },
        })

        const {
          content,
          totalPages: ctiTotalPages,
          totalElements: ctiTotalElements,
          numberOfElements: ctiNumberOfElements,
        } = normalizeCtiListingResponse(response?.data)

        if (!fetchFilters) {
          setTotalPages(ctiTotalPages ?? FIELD_VALUE_CONSTANTS.ONE)
          setTotalElements(ctiTotalElements ?? FIELD_VALUE_CONSTANTS.ZERO)
          setNumberOfElements(ctiNumberOfElements ?? FIELD_VALUE_CONSTANTS.ZERO)
          setIntergrationData(content.map(mapCtiIntegrationRow))
          dispatch(hideLoader())
          return {
            content,
            totalPages: ctiTotalPages,
            totalElements: ctiTotalElements,
            numberOfElements: ctiNumberOfElements,
          }
        }

        const accessor = (
          Array.isArray(searchCriteria)
            ? searchCriteria[0]?.search_key
            : searchCriteria?.search_key
        )?.toLowerCase()

        dispatch(hideLoader())

        return [
          ...new Set(
            content
              .map(mapCtiIntegrationRow)
              .map((row: DataTypeStore) =>
                getRowFilterValue(row, accessor ?? '')
              )
              .map(String)
          ),
        ]
      }

      const integrationFilter =
        !isCaseTab && integration?.value
          ? [
              {
                field_name: 'group_id',
                values: [integration?.value],
                operation: 'EQUALS',
              },
            ]
          : []

      const filterPayload = [
        {
          logical_operator: 'AND',
          criteria: [...rootFilters, ...integrationFilter],
        },
        {
          criteria: sortEntity === ROOT_ENTITY ? sortCriteria : [],
        },
      ]

      const caseExtraCriteria =
        isCaseTab && caseId
          ? [
              {
                field_name: 'id',
                operation: 'EQUALS',
                values: [caseId],
              },
            ]
          : []

      const listingPage = !fetchFilters ? page : pageInformation.current.page
      const listingSize = !fetchFilters ? size : pageInformation.current.size

      const res = await fetchGenericPayloadIntegrationLogsListing(
        listingPage,
        listingSize,
        filterPayload,
        buildRelatedFilters,
        caseExtraCriteria
      )

      if (!fetchFilters && res?.content) {
        setTotalPages(res?.totalPages ?? FIELD_VALUE_CONSTANTS.ONE)
        setTotalElements(res?.totalElements ?? FIELD_VALUE_CONSTANTS.ZERO)
        setNumberOfElements(res?.numberOfElements ?? FIELD_VALUE_CONSTANTS.ZERO)

        let listingContent = res.content as FieldValues[]

        if (isCaseTab && caseId) {
          const ocrChildContext = await resolveOcrChildFaxContextForCase(caseId)

          if (ocrChildContext.applies) {
            const parentResourceId = String(
              ocrChildContext.parentCommunicationRecordId
            )

            listingContent = listingContent.filter(
              (item) => String(item.resource_id) !== parentResourceId
            )

            const parentFilterPayload = [
              {
                logical_operator: 'AND',
                criteria: [
                  ...rootFilters,
                  {
                    field_name: 'resource_id',
                    operation: 'EQUALS',
                    values: [parentResourceId],
                  },
                ],
              },
              {
                criteria: sortEntity === ROOT_ENTITY ? sortCriteria : [],
              },
            ]

            const parentRes = await fetchGenericPayloadIntegrationLogsListing(
              page,
              size,
              parentFilterPayload,
              buildRelatedFilters
            )

            listingContent = mergeGenericPayloadLogItems(
              listingContent,
              parentRes.content
            )
          }
        }

        const mappedRows = listingContent.map((item: FieldValues) =>
          mapIntegrationRow(item)
        )

        setIntergrationData(mappedRows)
        dispatch(hideLoader())
        return { ...res, content: listingContent }
      }

      if (fetchFilters && res?.content) {
        const accessor = (
          Array.isArray(searchCriteria)
            ? searchCriteria[0]?.search_key
            : searchCriteria?.search_key
        )?.toLowerCase()

        dispatch(hideLoader())

        return [
          ...new Set(
            res.content
              .map((item: FieldValues) => mapIntegrationRow(item))
              .map((row: DataTypeStore) =>
                getRowFilterValue(row, accessor ?? '')
              )
              .map(String)
          ),
        ]
      }

      dispatch(hideLoader())
      return res
    } catch (err) {
      console.error(err)
      dispatch(hideLoader())
    }
  }
  const getIntegrationColumnsFilterOptions = async (
    searchCriteria: SearchCriteria
  ): Promise<string[]> => {
    const options = await fetchIntegrationData(
      selectedIntegration as IntegrationType,
      pageNumber,
      pageSize,
      sortDirection,
      searchCriteria,
      true
    )
    return (options ?? []) as string[]
  }

  const stringColumnFilter = {
    dataType: tableFilterType.STRING,
    onKeyPress: getIntegrationColumnsFilterOptions,
  }

  const dateColumnFilter = {
    dataType: tableFilterType.DATE_TIME,
    onKeyPress: getIntegrationColumnsFilterOptions,
  }

  const isCtiSelected = isCtiIntegration(selectedIntegration)
  const associatedCaseIdsColumn = buildAssociatedCaseIdsColumn(
    handleViewAssociatedCaseIds
  )
  const ivrAssociatedCaseIdsColumn =
    buildIvrAssociatedCaseIdsColumn(handleViewIvrCaseIds)

  const caseIdColumn: TableColumnType<DataTypeStore> = {
    header: INTEGRATION_LOGS.CASE_ID,
    accessor: 'case_id',
    type: 'component',
    isSortable: true,
    filterData: stringColumnFilter,
    renderComponent: (row: DataTypeStore) => <CaseIdLinkCell row={row} />,
  }

  const ctiColumns: TableColumnType<DataTypeStore>[] = [
    {
      header: INTEGRATION_LOGS.API_IDENTIFIER,
      accessor: 'api_identifier',
      type: 'text',
      isSortable: true,
      filterData: stringColumnFilter,
    },
    {
      header: INTEGRATION_LOGS.REQUEST,
      accessor: 'req',
      type: 'component',
      renderComponent: (row) => <IntegrationLogRequestCell row={row} />,
    },
    {
      header: INTEGRATION_LOGS.CALL_RECEIVED_TIMESTAMP,
      accessor: 'call_received_at',
      type: 'text',
      isSortable: true,
      filterData: dateColumnFilter,
    },
    {
      header: INTEGRATION_LOGS.INTERACTION_ID,
      accessor: 'interaction_id',
      type: 'text',
      isSortable: true,
      filterData: stringColumnFilter,
    },
    ...(!isCaseTab ? [associatedCaseIdsColumn] : []),
    {
      header: INTEGRATION_LOGS.AWS_CONTACT_ID,
      accessor: 'aws_contact_id',
      type: 'text',
      isSortable: true,
      filterData: stringColumnFilter,
    },
    {
      header: INTEGRATION_LOGS.PROGRAM,
      accessor: 'program',
      type: 'text',
      isSortable: true,
      filterData: stringColumnFilter,
    },
    {
      header: INTEGRATION_LOGS.PRODUCT,
      accessor: 'product',
      type: 'text',
      isSortable: true,
      filterData: stringColumnFilter,
    },
  ]

  const standardColumns: TableColumnType<DataTypeStore>[] = [
    {
      header: INTEGRATION_LOGS.API_IDENTIFIER,
      accessor: 'api',
      type: 'text',
      isSortable: true,
      filterData: stringColumnFilter,
    },
    {
      header: INTEGRATION_LOGS.REQUEST,
      accessor: 'req',
      type: 'component',
      renderComponent: (row) => <IntegrationLogRequestCell row={row} />,
    },
    {
      header: INTEGRATION_LOGS.STATUS,
      accessor: 'status',
      type: 'component',
      isSortable: true,
      filterData: stringColumnFilter,
      renderComponent: (row) => {
        return (
          <div
            className={`${
              row?.status === INTEGRATION_LOGS.STATUS_SUCCESS ||
              row?.status === INTEGRATION_LOGS.STATUS_DELIVERED
                ? 'bg-success-100'
                : 'bg-error-100'
            } p-1 rounded`}
          >
            <span>{row?.status}</span>
          </div>
        )
      },
    },
    {
      header: INTEGRATION_LOGS.HTTPS_STATUS_CODE,
      accessor: 'status_code',
      type: 'component',
      isSortable: true,
      filterData: stringColumnFilter,
      renderComponent: (row) => {
        const req = row?.req as IntegrationLogReq | undefined
        return row?.status_code !== 200 ? (
          <Tooltip
            content={
              selectedIntegration?.label === 'EXPERIAN'
                ? row?.status_code + ' ' + EXPERIAN_ERROR_MESSAGE
                : row?.status_code +
                  INTEGRATION_LOGS.REQUEST_FAILED +
                  req?.error
            }
          >
            <span>{row?.status_code ?? '--'}</span>
          </Tooltip>
        ) : (
          <Tooltip
            content={
              row?.status_code
                ? row?.status_code + INTEGRATION_LOGS.REQUEST_SUCCESS
                : '--'
            }
          >
            <span>{row?.status_code ?? '--'}</span>
          </Tooltip>
        )
      },
    },
    {
      header: INTEGRATION_LOGS.REQUEST_TYPE,
      accessor: 'request_type',
      type: 'text',
      isSortable: true,
      filterData: stringColumnFilter,
    },
    {
      header: INTEGRATION_LOGS.TRANSACTION_ID,
      accessor: 'transaction_id',
      type: 'text',
      isSortable: true,
      filterData: stringColumnFilter,
    },
    {
      header: INTEGRATION_LOGS.REQUEST_TIMESTAMP,
      accessor: 'request_timestamp',
      type: 'text',
      isSortable: true,
      filterData: dateColumnFilter,
    },
    {
      header: INTEGRATION_LOGS.RESPONSE_TIMESTAMP,
      accessor: 'response_timestamp',
      type: 'text',
      isSortable: true,
      filterData: dateColumnFilter,
    },
    {
      header: INTEGRATION_LOGS.PROCESSING_DURATION,
      accessor: 'processing_duration',
      type: 'text',
    },
    {
      header: INTEGRATION_LOGS.ENTITY_ID,
      accessor: 'entity_id',
      type: 'component',
      isSortable: true,
      filterData: stringColumnFilter,
      renderComponent: (row) => <PatientIdLinkCell row={row} />,
    },
    ...(!isCaseTab ? [caseIdColumn] : []),
    ...(!isCaseTab && isIvrSelected ? [ivrAssociatedCaseIdsColumn] : []),
    ...(!isCaseTab && !isIvrSelected
      ? [
          associatedCaseIdsColumn,
          {
            header: INTEGRATION_LOGS.COMMUNICATION_KEY,
            accessor: 'communication_key',
            type: 'text',
            isSortable: true,
            filterData: stringColumnFilter,
          } as TableColumnType<DataTypeStore>,
        ]
      : []),
    {
      header: INTEGRATION_LOGS.PROGRAM,
      accessor: 'program',
      type: 'text',
      isSortable: true,
      filterData: stringColumnFilter,
    },
    {
      header: INTEGRATION_LOGS.PRODUCT,
      accessor: 'product',
      type: 'text',
      isSortable: true,
      filterData: stringColumnFilter,
    },
    {
      header: INTEGRATION_LOGS.TRIGGERED_BY,
      accessor: 'triggered_by',
      type: 'text',
      isSortable: true,
      filterData: stringColumnFilter,
    },
  ]

  const columns = isCtiSelected ? ctiColumns : standardColumns

  const onExportOption = async () => {
    if (!selectedIntegration?.value || isCtiSelected) return

    dispatch(showLoader())
    try {
      const payload = buildIntegrationLogsExportPayload(
        currentFilterPayload.current,
        selectedIntegration.value,
        sortDirection
      )

      const { data } = await exportEntity2ChildData(payload)
      const logViewName = (selectedIntegration?.label ?? '').replace(
        /[^a-zA-Z0-9]/g,
        ''
      )
      const utcTimestamp = `${getCurrentUTCDate('YYYYMMDD_HHmmss')}Z`
      const fileName = `IntegrationLogs_${logViewName}_${utcTimestamp}.csv`

      downloadCSV(data, fileName)
      dispatch(
        addToaster({
          id: uniqueId(),
          message: NAMESPACES.EXPORT_SUCCESSFUL,
          type: ToasterType.SUCCESS,
        })
      )
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const onSortClick = (key: string, isAsc: SORT_KEYS) => {
    const apiField = getFieldNameForIntegration(key, isCtiSelected)
    setSortDirection({
      keyName: isAsc === SORT_KEYS.Disabled ? '' : apiField,
      direction: isAsc,
      key: isAsc === SORT_KEYS.Disabled ? '' : key,
      isAsc,
    })
  }

  const onFilterChange = async (value: TableFilterObjectBody) => {
    const nextCriteria = Object.values(value?.search_criteria ?? {}).filter(
      (criteria) => criteria && Object.keys(criteria).length > 0
    )
    const hadCriteria = currentFilterPayload.current.length > 0

    setApiFilter(value)

    if (!hadCriteria && nextCriteria.length === 0) {
      return
    }

    pageInformation.current.page = FIELD_VALUE_CONSTANTS.ZERO

    const response = await fetchIntegrationData(
      selectedIntegration as IntegrationType,
      FIELD_VALUE_CONSTANTS.ZERO,
      pageSize,
      sortDirection,
      nextCriteria
    )
    setPageNumber(FIELD_VALUE_CONSTANTS.ZERO)
    setTotalPages(response?.totalPages ?? FIELD_VALUE_CONSTANTS.ONE)
    setTotalElements(response?.totalElements ?? FIELD_VALUE_CONSTANTS.ZERO)
    setNumberOfElements(
      response?.numberOfElements ?? FIELD_VALUE_CONSTANTS.ZERO
    )
  }

  const handlePageChange = async (page: number, rowsPerPage: number) => {
    const nextPage =
      rowsPerPage !== pageSize
        ? FIELD_VALUE_CONSTANTS.ZERO
        : page - FIELD_VALUE_CONSTANTS.ONE

    if (
      nextPage === pageInformation.current.page &&
      rowsPerPage === pageInformation.current.size
    ) {
      return
    }

    pageInformation.current = { page: nextPage, size: rowsPerPage }

    const response = await fetchIntegrationData(
      selectedIntegration as IntegrationType,
      nextPage,
      rowsPerPage,
      sortDirection,
      currentFilterPayload.current
    )

    setPageNumber(nextPage)
    setPageSize(rowsPerPage)
    setTotalPages(response?.totalPages ?? FIELD_VALUE_CONSTANTS.ONE)
    setTotalElements(response?.totalElements ?? FIELD_VALUE_CONSTANTS.ZERO)
    setNumberOfElements(
      response?.numberOfElements ?? FIELD_VALUE_CONSTANTS.ZERO
    )
  }
  useEffect(() => {
    const currentIntegrationValue = isCaseTab
      ? caseId
      : selectedIntegration?.value

    const isIntegrationChange =
      prevIntegrationValue.current !== currentIntegrationValue

    if (isIntegrationChange) {
      prevIntegrationValue.current = currentIntegrationValue
      pageInformation.current = {
        page: FIELD_VALUE_CONSTANTS.ZERO,
        size: defaultPageSize,
      }
      setPageNumber(FIELD_VALUE_CONSTANTS.ZERO)
      setPageSize(defaultPageSize)
      setApiFilter({ fields: null, search_criteria: {} })
      currentFilterPayload.current = []

      const hadActiveSort = sortDirection.direction !== SORT_KEYS.Disabled
      if (hadActiveSort) {
        setSortDirection({
          key: '',
          keyName: '',
          direction: SORT_KEYS.Disabled,
          isAsc: SORT_KEYS.Disabled,
        })
        return
      }
    }

    const disabledSort: SortDirectionState = {
      key: '',
      keyName: '',
      direction: SORT_KEYS.Disabled,
      isAsc: SORT_KEYS.Disabled,
    }

    const generation = ++fetchGenerationRef.current

    const loadData = async () => {
      const page = isIntegrationChange
        ? FIELD_VALUE_CONSTANTS.ZERO
        : pageInformation.current.page
      const size = pageInformation.current.size
      const response = await fetchIntegrationData(
        selectedIntegration as IntegrationType,
        page,
        size,
        isIntegrationChange ? disabledSort : sortDirection,
        isIntegrationChange ? [] : currentFilterPayload.current
      )
      if (generation !== fetchGenerationRef.current) return
      setTotalPages(response?.totalPages ?? FIELD_VALUE_CONSTANTS.ONE)
      setTotalElements(response?.totalElements ?? FIELD_VALUE_CONSTANTS.ZERO)
      setNumberOfElements(
        response?.numberOfElements ?? FIELD_VALUE_CONSTANTS.ZERO
      )
    }
    loadData()
  }, [
    selectedIntegration?.value,
    sortDirection.key,
    sortDirection.direction,
    caseId,
    isCaseTab,
  ])

  return (
    <div className={`flex-1 min-h-0 flex flex-col w-full`}>
      <AssociatedCaseIdsModal
        isOpen={associatedCasesModal.isOpen}
        closeModal={() =>
          setAssociatedCasesModal({
            isOpen: false,
            cases: [],
            title: undefined,
          })
        }
        cases={associatedCasesModal.cases}
        title={
          associatedCasesModal.title ||
          INTEGRATION_LOGS.ASSOCIATED_CASE_IDS_TITLE
        }
      />
      {!isCtiSelected ? (
        <div className="flex justify-end mb-2 mt-[-2rem]">
          <ExportButtonDropdown
            showCSV
            disabled={!selectedIntegration}
            onExportOption={onExportOption}
            dataQaId="integration-logs-export-btn"
            customCSS="-translate-x-3/4"
          />
        </div>
      ) : null}
      <div className="flex-1 min-h-0 flex flex-col">
        <CustomAgTable
          columns={columns}
          data={intergrationData ?? []}
          tableName={tableName}
          onSortClick={onSortClick}
          sort={
            sortDirection.key && !isEmpty(sortDirection.key)
              ? { key: sortDirection.key, isAsc: sortDirection.isAsc }
              : null
          }
          filterObject={
            Object.keys(apiFilter.search_criteria ?? {}).length > 0
              ? apiFilter
              : undefined
          }
          onFilterChange={onFilterChange}
          pageName={''}
          paginationCurrentPage={pageNumber + FIELD_VALUE_CONSTANTS.ONE}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          rowsPerPage={pageSize}
          minHeight={isCaseTab ? '20rem' : ''}
          totalElements={totalElements}
          numberOfElements={numberOfElements}
        />
      </div>
    </div>
  )
}

export default memo(IntegrationLogsTable)
